import DownloadIcon from "@/assets/colorable/download.svg?react";
import ImageDollarIcon from "@/assets/colorable/image_dollar.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import VideoCodeForm from "@/components/Campaign/management/components/VideoCodeForm";
import Button from "@/components/Common/Button";
import useContentDownload from "@/hooks/useContentDownload";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {useCampaignGalleryStore} from "@/store/campaignGallery";
import {useManagementCampaign} from "@/store/campaignManagement";
import {TCarouselMedia} from "@/store/carousel";
import {isMoreBuyoutsModalOpenAtom, purchaseModalContentAtom} from "@/store/jotaiAtoms";
import {TCampaignApplicationReporting, TMediaGalleryItem, TPaginated} from "@/store/types";
import {InfiniteData, useQueryClient, type Query} from "@tanstack/react-query";
import {AxiosResponse} from "axios";
import {produce} from "immer";
import {useSetAtom} from "jotai";
import {FC, useState} from "react";
import {useTranslation} from "react-i18next";

const ContentPurchaseButton: FC<{
  containerClassName?: string;
  className?: string;
  contents: TCarouselMedia[];
  buyoutsPerContent?: number | null;
}> = ({containerClassName, contents, className, buyoutsPerContent = 1}) => {
  const {t} = useTranslation("common");
  const queryClient = useQueryClient();
  const campaignId = Number(useManagementCampaign().id);
  const setMoreBuyoutsModalOpen = useSetAtom(isMoreBuyoutsModalOpenAtom);
  const isAdmin = useIsAdmin();
  const resetGalleryMedia = useCampaignGalleryStore(state => state.resetMedia);
  const [purchasedIds, setPurchasedIds] = useState<Set<number>>(new Set());
  const hasPurchased =
    isAdmin ||
    contents[0].instagram_media?.media_product_type === "UGC" ||
    contents.every(content => !!content.instagram_media?.purchased || purchasedIds.has(content.id));
  const isGalleryQuery = (query: Query) =>
    String(query.queryKey[0]).startsWith(`/campaign/${campaignId}/media_gallery/`);
  const onPurchased = () => {
    const purchasedAt = new Date().toISOString();

    const purchasedMediaIds = new Set(
      contents.flatMap(content => [
        content.id,
        content.instagram_media?.id,
        ...(content.instagram_media?.children ?? []).map(child => child.id),
      ]),
    );

    const markPurchased = (medias?: {id: number; purchased?: string | null}[] | null) =>
      medias?.forEach(media => {
        if (purchasedMediaIds.has(media.id)) {
          media.purchased = purchasedAt;
        }
      });

    setPurchasedIds(previousIds => new Set([...previousIds, ...contents.map(content => content.id)]));
    resetGalleryMedia();

    queryClient.setQueriesData<InfiniteData<AxiosResponse<TPaginated<TCampaignApplicationReporting>>>>(
      {
        predicate: query =>
          String(query.queryKey[0]).startsWith(`/campaign/${campaignId}/reporting_accepted_applications/`),
      },
      previousState =>
        previousState &&
        produce(previousState, draft => {
          draft.pages.forEach(page =>
            page.data.results.forEach(application => {
              markPurchased(application.instagram_media?.posts);
              markPurchased(application.instagram_media?.reels);
              markPurchased(application.instagram_media?.source_materials);
              markPurchased(application.instagram_media?.stories);
              markPurchased(application.instagram_posts);
              markPurchased(application.instagram_reels);
              markPurchased(application.instagram_stories);
              markPurchased(application.source_materials);
              markPurchased(application.tiktok);
            }),
          );
        }),
    );

    queryClient.setQueriesData<InfiniteData<AxiosResponse<TPaginated<TMediaGalleryItem>>>>(
      {predicate: isGalleryQuery},
      previousState =>
        previousState &&
        produce(previousState, draft => {
          draft.pages.forEach(page => markPurchased(page.data.results.map(item => item.result)));
        }),
    );

    queryClient.setQueriesData<InfiniteData<AxiosResponse<TPaginated<{id: number; purchased?: string | null}>>>>(
      {predicate: query => String(query.queryKey[0]).startsWith("/media_library/?")},
      previousState =>
        previousState &&
        produce(previousState, draft => {
          draft.pages.forEach(page => markPurchased(page.data.results));
        }),
    );

    queryClient.setQueriesData<AxiosResponse<{id: number; purchased?: string | null}[]>>(
      {predicate: query => String(query.queryKey[0]).endsWith("/source_materials/")},
      previousState =>
        previousState &&
        produce(previousState, draft => {
          markPurchased(draft.data);
        }),
    );

    queryClient.refetchQueries({predicate: isGalleryQuery, type: "active"});
  };
  const setPurchaseContent = useSetAtom(purchaseModalContentAtom);
  const {remainingBuyouts} = usePackageLimits();
  const hasEnoughBuyouts = remainingBuyouts > 0;
  const {downloadMedia, isDownloading, progress} = useContentDownload(contents, contents[0].campaign?.title);

  return (
    <div className={clsxm("flex flex-col gap-1", containerClassName)}>
      <VideoCodeForm content={contents[0]} className={className} />
      {hasPurchased ? (
        <Button
          className={clsxm("h-12 gap-2", className)}
          shouldScaleOnHover={false}
          buttonStyle="Blue"
          isLoading={isDownloading}
          progress={progress}
          onClick={downloadMedia}>
          <DownloadIcon className="z-10 text-white" />
          <span className="z-10">{t("downloadContent")}</span>
        </Button>
      ) : (
        <div className="flex items-end">
          <div className="border-primary-light-solid text-neutral-black bg-neutral-grey-lightest flex h-12 items-center justify-center gap-1 rounded-l-md border px-3">
            <ImageDollarIcon className="size-3 shrink-0" />
            <span className="text-sm font-semibold">{buyoutsPerContent}</span>
          </div>
          <Button
            className={clsxm("h-12 gap-2 rounded-l-none", className)}
            shouldScaleOnHover={false}
            buttonStyle="Blue"
            onClick={() => {
              if (hasEnoughBuyouts) {
                setPurchaseContent({buyoutsPerContent: buyoutsPerContent!, contents, onPurchased});
              } else {
                setMoreBuyoutsModalOpen({show: true, type: "EXTRA_BUYOUTS"});
              }
            }}>
            <span className="z-10 text-wrap">{t(hasEnoughBuyouts ? "useBuyout" : "purchaseBuyouts")}</span>
            <RightArrowIcon className="z-10 text-white" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ContentPurchaseButton;
